// pages/Personal Attendance/components/DeptAttendanceTable.js
//
// Shared table used by every "Show *FTO/COA* of my Dept" + Schedule + YTD page.
// Renders a simple table of attendance rows with a future-only filter,
// a free-text search, and an empty state. The PARENT page picks the
// API call and passes `fetcher`, `title`, `kind` ("FTO" | "COA"), and
// optional `defaultFuture` / `showYearSelector`.

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ColumnFilterRow, applyColumnFilters } from "../../../Components/Common/DashboardKit";
import useTableSort from "../../../Components/Common/DashboardKit/useTableSort";
import SortableTh from "../../../Components/Common/DashboardKit/SortableTh";
import exportRowsToCsv from "../../../Components/Common/DashboardKit/exportCsv";
import {
    Container, Card, CardBody, Input, InputGroup, Spinner, Badge, Alert,
    Form, FormGroup, Label,
} from "reactstrap";
import {
    DashboardPageHeader, EmptyState, StandardTableToolbar,
} from "../../../Components/Common/DashboardKit";

const fmtDate = (d) => {
    if (!d) return "—";
    try { return new Date(d).toLocaleDateString(); } catch { return "—"; }
};
const StatusPill = ({ supv, hr }) => {
    // Approval composite: supervisor approval AND HR approval.
    if (supv === false || hr === false) return <Badge color="danger" pill>Denied</Badge>;
    if (supv === true && hr === true)   return <Badge color="success" pill>Approved</Badge>;
    if (supv === true && hr == null)    return <Badge color="info" pill>Awaiting HR</Badge>;
    return <Badge color="warning" pill>Pending</Badge>;
};

const DeptAttendanceTable = ({
    title,
    subtitle,
    kind,                 // "FTO" | "COA"
    fetcher,              // (params) => Promise<{ data: rows[] }>
    breadcrumb = [],
    defaultFuture = false,
    showFutureToggle = true,
    showYearSelector = false,
}) => {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState("");
    const [future, setFuture] = useState(defaultFuture);
    const [year, setYear] = useState(new Date().getFullYear());

    useEffect(() => {
        let alive = true;
        setLoading(true); setError(null);
        const params = {};
        if (future) params.future = true;
        if (showYearSelector) params.year = year;
        fetcher(params)
            .then(({ data }) => {
                if (!alive) return;
                setRows(Array.isArray(data) ? data : (data?.items ?? []));
            })
            .catch((e) => { if (alive) setError(e?.response?.data?.error || e?.message || "Failed to load"); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [future, year, fetcher, showYearSelector]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((r) =>
            String(r.empId ?? "").toLowerCase().includes(q)
            || String(r.empName ?? "").toLowerCase().includes(q)
            || String(r.employeeComment ?? "").toLowerCase().includes(q)
        );
    }, [rows, search]);

    // Round 34 — EPO-style per-column filters + click-to-sort (covers all
    // four department views: FTOs, COAs, YTD FTO, Schedules). The columns
    // adapt to `kind`, matching the conditional headers below.
    const [columnFilters, setColumnFilters] = useState({});
    const tblColumns = useMemo(() => {
        const cols = [
            { id: "empId",   label: "EmpID", type: "text", get: (r) => String(r.empId ?? "") },
            { id: "startDate", label: "Start", type: "date", get: (r) => r.startDate || "" },
            { id: "endDate",   label: "End",   type: "date", get: (r) => r.endDate || "" },
        ];
        if (kind === "FTO") cols.push({ id: "type", label: "Type", type: "text", get: (r) => r.type || "" });
        if (kind === "COA") cols.push({ id: "mod",  label: "Modified Times", type: "text", get: (r) => `${r.modifiedStartTime ?? ""} ${r.modifiedEndTime ?? ""}`.trim() });
        cols.push(
            { id: "status",  label: "Status",  type: "text", get: (r) => `${r.supervisorApproval ?? ""} ${r.hrApproval ?? ""}`.trim() },
            { id: "comment", label: "Comment", type: "text", get: (r) => r.employeeComment || "" },
        );
        return cols;
    }, [kind]);
    const filteredBase = applyColumnFilters(filtered, tblColumns, columnFilters);
    const { sorted: view, sort, toggleSort } = useTableSort(filteredBase, tblColumns);

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader title={title} subtitle={subtitle || `${rows.length} record(s)`} breadcrumb={breadcrumb} />
                {error && <Alert color="danger" className="mt-2">{error}</Alert>}

                <Card className="mt-3">
                    <CardBody>
                        <StandardTableToolbar
                            left={
                                <Form inline className="d-flex align-items-center gap-3">
                                    {showFutureToggle && (
                                        <FormGroup check inline className="mb-0">
                                            <Input type="checkbox" id="future-only"
                                                checked={future}
                                                onChange={(e) => setFuture(e.target.checked)} />
                                            <Label check for="future-only">Future only</Label>
                                        </FormGroup>
                                    )}
                                    {showYearSelector && (
                                        <FormGroup className="mb-0 d-flex align-items-center gap-2">
                                            <Label className="mb-0">Year</Label>
                                            <Input type="number" bsSize="sm" style={{ width: 100 }}
                                                value={year}
                                                onChange={(e) => setYear(parseInt(e.target.value) || year)} />
                                        </FormGroup>
                                    )}
                                </Form>
                            }
                            right={
                                {/* Round 34 — search replaced by the per-column filter row */}
                            }
                        />

                        {loading ? (
                            <div className="text-center py-5"><Spinner color="primary" /></div>
                        ) : filtered.length === 0 ? (
                            <EmptyState
                                title="No records to show"
                                description={future ? "Nothing scheduled in the future." : "Try adjusting your filters."}
                            />
                        ) : (
                            <>
                            {/* Round 29 — CSV of the filtered list (client-side rows) */}
                            <div className="d-flex justify-content-end mb-2">
                                <button
                                    type="button"
                                    className="btn btn-soft-primary btn-sm"
                                    onClick={() =>
                                        exportRowsToCsv(
                                            "attendance.csv",
                                            ((r0) => Object.keys(r0 || {}).map((k) => ({ key: k, label: k })))(view[0]),
                                            view
                                        )
                                    }
                                >
                                    <i className="ri-download-2-line align-bottom me-1" />Export CSV ({view.length})
                                </button>
                            </div>
                            <div className="table-responsive">
                                <table className="table table-striped table-bordered mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            {tblColumns.map((c) => (
                                                <SortableTh key={c.id} col={c} sort={sort} onToggle={toggleSort} />
                                            ))}
                                        </tr>
                                        <ColumnFilterRow columns={tblColumns} filters={columnFilters} onChange={setColumnFilters} />
                                    </thead>
                                    <tbody>
                                        {view.map((r) => (
                                            <tr
                                                key={r.id ?? `${r.empId}-${r.startDate}`}
                                                onClick={() => {
                                                    if (!r.id) return;
                                                    navigate(kind === "COA"
                                                        ? `/attendance/coa/${r.id}`
                                                        : `/attendance/fto/${r.id}`);
                                                }}
                                                style={{ cursor: r.id ? "pointer" : "default" }}
                                                title={r.id ? "Open request to review / approve" : ""}
                                            >
                                                <td>{r.empId}</td>
                                                <td>{fmtDate(r.startDate)}</td>
                                                <td>{fmtDate(r.endDate)}</td>
                                                {kind === "FTO" && <td>{r.type ?? "—"}</td>}
                                                {kind === "COA" && (
                                                    <td>{r.modifiedStartTime ?? "—"}{r.modifiedEndTime ? " → " + r.modifiedEndTime : ""}</td>
                                                )}
                                                <td><StatusPill supv={r.supervisorApproval} hr={r.hrApproval} /></td>
                                                <td style={{ maxWidth: 280 }} className="text-truncate" title={r.employeeComment ?? ""}>{r.employeeComment ?? "—"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            </>
                        )}
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
};

export default DeptAttendanceTable;
